import React, { useCallback, useEffect, useRef, useState } from "react";
import { Inbox, RefreshCw, Search, X } from "lucide-react";

const RequestsChrome = ({
  replyCount,
  outboxCount,
  totalCount,
  search,
  onSearchChange,
  refreshing,
  onRefresh,
}) => {
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  const titleLine = [
    replyCount > 0 ? `${replyCount} need reply` : "Inbox clear",
    `${outboxCount} sent`,
    `${totalCount} total`,
  ].join(" · ");

  return (
    <header className="req-chrome">
      <div className="req-chrome-row">
        <div className="req-chrome-copy">
          <p className="req-chrome-eyebrow">
            <Inbox size={9} aria-hidden />
            P2P invites
          </p>
          <h1 className="req-chrome-heading">P2P requests</h1>
          <span className="req-chrome-title-line">{titleLine}</span>
        </div>

        <div className="req-chrome-actions-wrap">
          <div
            ref={searchWrapRef}
            className={`req-chrome-search ${isExpanded ? "req-chrome-search-open" : ""} ${
              hasQuery ? "req-chrome-search-active" : ""
            }`}
          >
            {!isExpanded ? (
              <button
                type="button"
                className="req-chrome-search-toggle"
                onClick={openSearch}
                aria-label="Search requests"
                aria-expanded={false}
              >
                <Search size={13} strokeWidth={2.3} aria-hidden />
              </button>
            ) : (
              <>
                <Search size={13} aria-hidden className="req-chrome-search-icon" />
                <input
                  ref={inputRef}
                  type="search"
                  className="req-chrome-search-input"
                  placeholder="Match or person…"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onBlur={handleBlur}
                  aria-label="Search requests"
                  aria-expanded
                />
                {hasQuery ? (
                  <button
                    type="button"
                    className="req-chrome-search-clear"
                    onClick={() => onSearchChange("")}
                    aria-label="Clear search"
                  >
                    <X size={12} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="req-chrome-search-clear"
                    onClick={closeSearch}
                    aria-label="Close search"
                  >
                    <X size={12} />
                  </button>
                )}
              </>
            )}
          </div>

          <button
            type="button"
            className="req-chrome-btn"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh requests"
            title="Refresh"
          >
            <RefreshCw size={13} strokeWidth={2.3} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default RequestsChrome;
